import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { EComplaintStatus } from '../../utils/enums';
import { ApiService } from './api.service';
import {Timeline} from './cases.service';

interface IComplaintResponse {
  message: string;
  payload: IPayload;
}

interface ICreateComplaintResponse {
  message: string;
  payload: {
    complaintId: string;
    applicationNumber: string;
  };
}

export interface ISingleComplaintResponse {
  message: string;
  payload: IComplaintDetail;
}

interface IPage {
  size: number;
  number: number;
  totalElements: number;
  totalPages: number;
}

interface IPayload {
  content: IContent[];
  page: IPage;
}

interface IContent {
  id: string;
  title: string;
  status: EComplaintStatus;
  description: string;
  createdAt: string;
  createdFor: string;
}

export interface IComplaint {
  title: string;
  description: string;
  location: ILocation;
  supportingDocuments: ISupportingDocument[];
  contact: IContact;
}

export interface IComplaintDetail {
  id: string;
  title: string;
  description: string;
  type: string;
  location: ILocation;
  trackingLevel: null;
  contact: IContact;
  status: EComplaintStatus;
  urgencyLevel: string;
  categories: {
    name: string;
    id: string;
  }[];
  documents: { type: string; url: string; fileName: string }[];
  followUps: IFollowUp[];
  createdAt: string;
  caseType: string;
  victimsNumber: string;
  estimatedDays: string;
  organizations: {
    organizationName: string;
    organizationId: string;
  }[];
  rejectionReason: string;
  acknowledgedBy?: string | null;
  acknowledgedAt?: string | null;
  acknowledgedComment?: string | null;
}

export interface IFollowUp {
  comment: string;
  createdByUserName: string;
  position: string;
  action: string;
  createdAt: Date;
  summary: string;
}

interface ILocation {
  provinceName?: string;
  provinceId: string;
  districtName?: string;
  districtId: string;
  sectorName?: string;
  sectorId: string;
  cellName?: string;
  cellId: string;
  villageName?: string;
  villageId: string;
}

export interface ISupportingDocument {
  fileName: string;
  type: string;
  url?: string;
}

interface IContact {
  id?: string;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  documentType?: string;
  documentNumber?: string;
  gender?: string;
}

export interface ICaseTrackingResponse {
  payload: ICaseTrackingPayload;
  message: string;
}




export interface ICaseTrackingPayload {
  title: string;
  description: string;
  location: ILocation;
  contact: IContact;
  createdAt: Date;
  acknowledgedBy: string,
  acknowledgedAt: string,
  acknowledgedComment: string,
  complaintCase: IComplaintCase;
  isClosed: boolean;
}

export interface IComplaintCase {
  status: EComplaintStatus;
  severity: string;
  trackingLevel: string;
  estimatedResolutionDate: Date;
  estimatedResolutionDays: number;
  isUrgent: boolean;
  owner: string;
  userId: string;
  timeline: Timeline[];
  informedInstitutions: IInformedInstitution[];
  primaryOrganization: {
    id: string;
    name: string;
    initialisms: string;
    displayName: string;
    type: string;
  };
  supportingDocuments: ISupportingDocument[];
}

export interface IInformedInstitution {
  id: string;
  name: string;
  type: string;
}

export interface ITimeline {
  action: string;
  actionMetadata: IActionMetadata;
  performedByUserId: string;
  createdAt: Date;
  visibility:  'PUBLIC' | 'PRIVATE';
  userDetails?: {
    fullName: null,
    email: null,
    phoneNumber: null,
    occupation: null,
    profilePictureUrl: null,
    institution: null,
    assignedLocations: null,
    trackingLevel: null
  }
}

export interface IActionMetadata {
  reportedBy?: string;
  createdBy?: string;
  invitationNote?: string;
  comment?: string;
  CommentedBy?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ComplaintsService {
  constructor(private api: ApiService) {}

  createComplaint(complaint: IComplaint): Observable<ICreateComplaintResponse> {
    return this.api.POST<ICreateComplaintResponse>('complaints', complaint);
  }

  getComplaints(page: number, size: number): Observable<IComplaintResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.api.GET<IComplaintResponse>('complaints', { params });
  }

  getPendingComplaints(
    page: number,
    size: number,
    scope?: string
  ): Observable<IComplaintResponse> {
    let params = new HttpParams()
      .set('status', 'PENDING')
      .set('page', page.toString())
      .set('size', size.toString());

    if (scope) {
      params = params.set('scope', scope);
    }

    return this.api.GET<IComplaintResponse>('complaints', { params });
  }

  getComplaint(complaintId: string): Observable<IComplaintDetail> {
    return this.api.GET<ISingleComplaintResponse>(`complaints/${complaintId}`).pipe(map((response) => response.payload));
  }

  updateComplaint(
    complaintId: string,
    complaint: IComplaint
  ): Observable<ISingleComplaintResponse> {
    return this.api.PATCH<ISingleComplaintResponse>(
      `complaints/${complaintId}/citizen`,
      complaint
    );
  }

  getOngoingComplaintsCount(): Observable<IComplaintResponse> {
    const params = new HttpParams().set('status', 'ONGOING');
    return this.api.GET<IComplaintResponse>('complaints', { params });
  }

  getVerificationCode(applicationNumber: string): Observable<{
    message: string;
    payload: {
      phoneNumber: string;
    };
  }> {
    const payload = {
      applicationNumber: applicationNumber,
    };
    return this.api.POST<{
      message: string;
      payload: { phoneNumber: string };
    }>('complaints/get-complaint-tracking-otp', payload);
  }

  getComplaintDetailsWithOtp(
    applicationNumber: string,
    otp: string
  ): Observable<ICaseTrackingPayload> {
    const params = new HttpParams()
      .set('applicationNumber', applicationNumber)
      .set('otpCode', otp);

    return this.api.GET<ICaseTrackingResponse>('complaints/tracking', { params }).pipe(map((response) => response.payload));
  }

  closeComplaint(
    complaintId: string,
    comment: string
  ): Observable<IComplaintResponse> {
    return this.api.PATCH<IComplaintResponse>(`complaints/${complaintId}/close`, { comment });
  }
}
